import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { compose, patch } from '@ngxs/store/operators';
import { getYear, subYears } from 'date-fns';
import { groupBy, min } from 'lodash';
import { DscrResult } from 'ng2-app/models/dscr/DscrResult.struct';
import { ProfitLossStatement } from 'ng2-app/models/dscr/ProfitLossStatementItem.struct';
import { DscrService } from 'ng2-app/services/dscr/dscr.service';
import { upsertItem } from 'ng2-app/store/operators/upsert.operator';
import {
  GetDscrResults,
  GetProfitLossStatements,
  CreateProfitLossStatementItem, ProcessDscrResults,
} from 'ng2-app/store/profit-loss-statements/dscr.actions';
import { catchError, tap } from 'rxjs/operators';

export interface DscrStateModel {
  loading: boolean;
  dscrResults: DscrResult[];
  profitLossStatementItems: any[];
}

@State<Partial<DscrStateModel>>({
  name: 'Dscr',
  defaults: {
    loading: false,
    dscrResults: [],
    profitLossStatementItems: [],
  },
})
@Injectable({
  providedIn: 'root',
})
export class DscrState {

  @Selector()
  static maxNewLoanAmountsByLender(state: DscrStateModel) {
    const years = Array.from({length: 2}, (__, i) => getYear(subYears(Date.now(), i)));
    const results = state.dscrResults || [];
    const validResults = results.filter(dscrResult => years.includes(dscrResult.year));
    const byLenderId = groupBy(validResults, 'lenderId');
    return Object.keys(byLenderId).map(lenderId => {
      const result = byLenderId[lenderId];
      const lenderName = result[0]?.lenderName;
      const maxNewLoan = min( result.map(item => item.maxNewLoanAmount)) as number;
      return {
        lenderId,
        lenderName,
        maxNewLoanAmount: Math.max(maxNewLoan, 0),
      };
    });
  }

  @Selector()
  static dscrResults(state: DscrStateModel) {
    return state.dscrResults;
  }

  @Selector()
  static profitLossStatements(state: DscrStateModel) {
    return state.profitLossStatementItems;
  }

  @Selector()
  static currentYearProfitLoss(state: DscrStateModel) {
    const year = getYear(Date.now());
    const items = state.profitLossStatementItems.filter( pl => pl.year === year && !pl.loanProductId);
    const attributes = [
      {
        alias: 'months',
        title: 'Months',
        fieldId: 20,
      },
      {
        alias: 'revenue',
        title: 'Revenue',
        fieldId: 1,
      },
      {
        alias: 'compensationOfOfficers',
        title: 'Officer Compensation',
        fieldId: 4,
      },
      {
        alias: 'netIncome',
        title: 'Net Income',
        fieldId: 3,
      },
      {
        alias: 'costOfGoodsSold',
        title: 'Cost of Goods Sold',
        fieldId: 2,
      },
      {
        alias: 'businessInterest',
        title: 'Interest',
        fieldId: 5,
      },
      {
        alias: 'depreciation',
        title: 'Depreciation',
        fieldId: 6,
      },
      {
        alias: 'amortization',
        title: 'Amortization',
        fieldId: 7,
      },
    ];
    return attributes.map(attr => {
      const item = items.find( plItem => plItem.profitLossStatementFieldId === attr.fieldId);
      return {
        ...attr,
        value: item?.profitLossStatementFieldValue,
      };
    }, {});
  }

  @Selector()
  static loading(state: DscrStateModel) {
    return state.loading;
  }

  constructor(
    private dscrService: DscrService,
  ) {}

  @Action(GetDscrResults)
  GetDscrResults(
    ctx: StateContext<DscrStateModel>,
    { borrowerId }: GetDscrResults,
  ) {
    ctx.patchState({loading: true});
    return this.dscrService.getDscrResults(borrowerId)
      .pipe(
        catchError(err => {
          ctx.patchState({loading: false});
          throw err;
        }),
        tap(dscrResults => {
          ctx.patchState({
            loading: false,
            dscrResults,
          });
        }),
      );
  }

  @Action(GetProfitLossStatements)
  GetProfitLossStatements(
    ctx: StateContext<DscrStateModel>,
    { borrowerId }: GetProfitLossStatements,
  ) {
    ctx.patchState({loading: true});
    return this.dscrService.getProfitLossStatements(borrowerId)
      .pipe(
        catchError(err => {
          ctx.patchState({loading: false});
          throw err;
        }),
        tap(profitLossStatementItems => {
          ctx.patchState({
            loading: false,
            profitLossStatementItems,
          });
        }),
      );
  }

  @Action(CreateProfitLossStatementItem)
  CreateProfitLossStatementItem(
    ctx: StateContext<DscrStateModel>,
    { borrowerId, profitLossStatementItem }: CreateProfitLossStatementItem,
  ) {
    ctx.patchState({loading: true});
    return this.dscrService.createProfitLossStatement(borrowerId, profitLossStatementItem)
      .pipe(
        catchError(err => {
          ctx.patchState({loading: false});
          throw err;
        }),
        tap(items => {
          const upsert = items.map(item => upsertItem<ProfitLossStatement>(existing => existing.id === item.id, item));
          ctx.setState(patch({
            loading: false,
            profitLossStatementItems: compose(...upsert),
          }));
        }),
      );
  }

  @Action(ProcessDscrResults)
  ProcessDscrResults(
    ctx: StateContext<DscrStateModel>,
    { borrowerId }: ProcessDscrResults,
  ) {
    ctx.patchState({loading: true});
    return this.dscrService.processDscrResults(borrowerId)
      .pipe(
        catchError(err => {
          ctx.patchState({loading: false});
          throw err;
        }),
        tap(dscrResults => {
          ctx.patchState({
            loading: false,
            dscrResults,
          });
        }),
      );
  }

}
